import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LessonRegisterRef, MarkPeriodBody, MarkPeriodBulkBody, RoleGroup, attendanceApi } from '@/services';

export const attendanceKeys = {
  all: ['admin', 'attendance'] as const,
  daily: (date: string, role: RoleGroup, gradeLevel?: number, search?: string) =>
    ['admin', 'attendance', 'daily', date, role, gradeLevel, search] as const,
  summary: (date: string) => ['attendance', 'summary', date] as const,
  myHomeroomGrades: () => ['admin', 'attendance', 'my-homeroom-grades'] as const,
  person: (userId: string, from: string, to: string) => ['admin', 'attendance', 'person', userId, from, to] as const,
  report: (from: string, to: string, role: RoleGroup, gradeLevel?: number) =>
    ['admin', 'attendance', 'report', from, to, role, gradeLevel] as const,
  calendar: (from: string, to: string) => ['admin', 'attendance', 'calendar', from, to] as const,
  myPeriods: (date: string) => ['admin', 'attendance', 'my-periods', date] as const,
  periodRoster: (classId: string, periodId: string, date: string) =>
    ['admin', 'attendance', 'period-roster', classId, periodId, date] as const,
  periodDay: (classId: string, date: string) => ['admin', 'attendance', 'period-day', classId, date] as const,
  subjectRates: (gradeLevel?: number, classId?: string, subjectId?: string) =>
    ['admin', 'attendance', 'subject-rates', gradeLevel, classId, subjectId] as const,
  audit: (from: string, to: string, gradeLevel?: number) =>
    ['admin', 'attendance', 'audit', from, to, gradeLevel] as const,
};

export function useAttendanceDailyQuery(
  params: {
    date: string;
    role: RoleGroup;
    gradeLevel?: number;
    search?: string;
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: attendanceKeys.daily(params.date, params.role, params.gradeLevel, params.search),
    queryFn: () => attendanceApi.daily(params),
    enabled: options?.enabled ?? true,
  });
}

export function useAttendanceSummaryQuery(date: string) {
  return useQuery({
    queryKey: attendanceKeys.summary(date),
    queryFn: () => attendanceApi.getSummary(date),
  });
}

export function useMarkAttendanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      userId: string;
      date: string;
      status: 'present' | 'absent' | 'late';
      checkInTime?: string | null;
      checkOutTime?: string | null;
      note?: string | null;
    }) => attendanceApi.mark(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}

export function useAttendancePersonQuery(userId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: attendanceKeys.person(userId ?? '', from, to),
    queryFn: () => attendanceApi.personHistory(userId!, from, to),
    enabled: Boolean(userId),
  });
}

export function useAttendanceReportQuery(
  params: { from: string; to: string; role: RoleGroup; gradeLevel?: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: attendanceKeys.report(params.from, params.to, params.role, params.gradeLevel),
    queryFn: () => attendanceApi.report(params),
    enabled: options?.enabled,
  });
}

export function useSchoolCalendarQuery(from: string, to: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: attendanceKeys.calendar(from, to),
    queryFn: () => attendanceApi.listCalendar(from, to),
    enabled: options?.enabled,
  });
}

export function useUpsertCalendarMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      date: string;
      kind: 'open' | 'closed';
      scope?: string;
      audience?: 'all' | 'staff' | 'students';
      label?: string;
      bellScheduleVariantId?: string | null;
    }) => attendanceApi.upsertCalendar(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}

export function useRemoveCalendarMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => attendanceApi.removeCalendar(date),
    onSuccess: () => qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}

// ─── Period-attendance hooks ──────────────────────────────────────────────────

/**
 * Cached hard: a timetable changes a few times a year, and this runs on every page as part
 * of deciding what the sidebar offers.
 */
export function useTeachesAnyQuery(enabled = true) {
  return useQuery({
    queryKey: ['attendance', 'teaches-any'],
    queryFn: () => attendanceApi.teachesAny(),
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

export function useMyPeriodsQuery(date: string) {
  return useQuery({
    queryKey: attendanceKeys.myPeriods(date),
    queryFn: () => attendanceApi.myPeriods(date),
    enabled: Boolean(date),
  });
}

export function useMyHomeroomGradesQuery() {
  return useQuery({
    queryKey: attendanceKeys.myHomeroomGrades(),
    queryFn: () => attendanceApi.myHomeroomGrades(),
  });
}

export function usePeriodRosterQuery(classId: string | undefined, periodId: string | undefined, date: string) {
  return useQuery({
    queryKey: attendanceKeys.periodRoster(classId ?? '', periodId ?? '', date),
    queryFn: () => attendanceApi.periodRoster(classId!, periodId!, date),
    enabled: Boolean(classId) && Boolean(periodId) && Boolean(date),
  });
}

export function usePeriodDayQuery(classId: string | undefined, date: string) {
  return useQuery({
    queryKey: attendanceKeys.periodDay(classId ?? '', date),
    queryFn: () => attendanceApi.periodDay(classId!, date),
    enabled: Boolean(classId) && Boolean(date),
  });
}

export function useSubjectRatesQuery(params: { gradeLevel?: number; classId?: string; subjectId?: string }) {
  return useQuery({
    queryKey: attendanceKeys.subjectRates(params.gradeLevel, params.classId, params.subjectId),
    queryFn: () => attendanceApi.subjectRates(params),
  });
}

export function useAttendanceAuditQuery(params: { from: string; to: string; gradeLevel?: number }) {
  return useQuery({
    queryKey: attendanceKeys.audit(params.from, params.to, params.gradeLevel),
    queryFn: () => attendanceApi.audit(params),
    enabled: Boolean(params.from && params.to),
  });
}

/** A dry run only counts; a real fill changes every report that reads lesson marks. */
export function useFillLessonRegistersMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { registers: LessonRegisterRef[]; dryRun?: boolean }) => attendanceApi.fillLessons(data),
    onSuccess: (_res, variables) => {
      if (!variables.dryRun) void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

export function useMarkPeriodMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MarkPeriodBody) => attendanceApi.markPeriod(data),
    onSuccess: (_res, variables) => {
      void qc.invalidateQueries({
        queryKey: attendanceKeys.periodRoster(variables.classId, variables.periodId, variables.date),
      });
      void qc.invalidateQueries({ queryKey: attendanceKeys.myPeriods(variables.date) });
    },
  });
}

export function useMarkPeriodBulkMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MarkPeriodBulkBody) => attendanceApi.markPeriodBulk(data),
    onSuccess: (_res, variables) => {
      void qc.invalidateQueries({
        queryKey: attendanceKeys.periodRoster(variables.classId, variables.periodId, variables.date),
      });
      void qc.invalidateQueries({ queryKey: attendanceKeys.myPeriods(variables.date) });
    },
  });
}

export function useRecordCheckinTemperatureMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { studentId: string; classId: string; date: string; tempC: number; note?: string }) =>
      attendanceApi.recordCheckinTemperature(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}
